import { type ComponentType, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';

import { readableColor } from 'polished';

import { ActionListLink, StaticPageLink } from '@/common/links';
import BadgeTooltip from '@/components/common/BadgeTooltip';
import PopoverTip from '@/components/common/PopoverTip';
import { CATEGORY_TYPE_FRAGMENT } from '@/fragments/category-tags.fragment';
import { CATEGORY_TAG_FRAGMENT } from '@/fragments/category.fragment';

const Categories = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$compact ? 'row' : 'column')};
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spaces.s050};
  font-size: ${(props) => props.theme.fontSizeMd};
  a {
    margin-right: ${(props) => props.theme.spaces.s050};
  }

  h3 {
    font-size: ${(props) => (props.$compact ? props.theme.fontSizeSm : props.theme.fontSizeBase)};
    margin-bottom: ${(props) =>
      props.$compact ? props.theme.spaces.s025 : props.theme.spaces.s100};
  }
`;

const CategoryGroup = styled.div<{ $compact?: boolean }>`
  margin-bottom: ${(props) => (props.$compact ? props.theme.spaces.s050 : props.theme.spaces.s200)};
`;

const CategoryBadges = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spaces.s050};
`;

type CategoryFilter = { typeIdentifier: string; categoryId: string };

type ListLinkComponentProps = PropsWithChildren<{
  categoryFilters: CategoryFilter[];
  filterGroupLabel?: string;
  filterValueLabel?: string;
}>;

type CategoryLinkMode = 'default' | 'indicator-list';

type CategoryLinkProps<ExtraCatProps = object, ExtraCTProps = object> = {
  category: {
    id: string;
    name: string;
    categoryPage?: null | {
      urlPath: string;
    };
  } & ExtraCatProps;
  categoryType: {
    identifier: string;
  } & ExtraCTProps;
  categoryTypeHeader?: string | null;
  ListLinkComponent?: ComponentType<ListLinkComponentProps>;
  linkMode?: CategoryLinkMode;
};

/**
 * A link to the category's page, or to the list filtered by the category.
 * Passes other props (the tooltip's aria and event props, ref) on to the link.
 */
function CategoryLink(props: PropsWithChildren<CategoryLinkProps>) {
  const {
    category,
    categoryType,
    categoryTypeHeader,
    ListLinkComponent = ActionListLink,
    linkMode = 'default',
    ...linkProps
  } = props;

  const filters = [
    {
      typeIdentifier: categoryType.identifier,
      categoryId: category.id,
    },
  ];

  if (linkMode === 'indicator-list') {
    return (
      <ListLinkComponent
        categoryFilters={filters}
        filterGroupLabel={categoryTypeHeader || categoryType.identifier}
        filterValueLabel={category.name}
        {...linkProps}
      />
    );
  }

  if (category.categoryPage) {
    return <StaticPageLink page={category.categoryPage} {...linkProps} />;
  }

  return <ActionListLink categoryFilters={filters} {...linkProps} />;
}

const Identifier = styled.span`
  color: ${(props) =>
    readableColor(
      props.theme.badgeColorNeutral,
      props.theme.graphColors.grey070,
      props.theme.graphColors.grey020
    )};
`;

/**
 * The minimal category shape CategoryContent renders. Structural on purpose:
 * both the category-tag fragments and the heavier category fragments (e.g.
 * the ones on attribute values) satisfy it, and the attribute types passed
 * as `categoryType` only need to provide the identifier used for links.
 */
type CategoryContentCategory = {
  id: string;
  identifier: string;
  name: string;
  helpText?: string | null;
  color?: string | null;
  iconSvgUrl?: string | null;
  iconImage?: { rendition?: { src?: string | null } | null } | null;
  categoryPage?: { urlPath: string } | null;
  type: { hideCategoryIdentifiers: boolean };
  parent?: CategoryContentCategory | null;
};

type CategoryContentProps = {
  categories: CategoryContentCategory[];
  categoryType: { identifier: string };
  categoryTypeHeader?: string | null;
  noLink?: boolean;
  compact?: boolean;
  ListLinkComponent?: ComponentType<ListLinkComponentProps>;
  linkMode?: CategoryLinkMode;
};

export const CategoryContent = (props: CategoryContentProps) => {
  const {
    categories,
    categoryType,
    categoryTypeHeader,
    noLink = false,
    compact = false,
    ListLinkComponent,
    linkMode = 'default',
  } = props;

  return (
    <CategoryBadges>
      {categories.map((item) => (
        <li key={item.id}>
          <BadgeTooltip
            id={item.id}
            tooltip={item.helpText ?? undefined}
            content={
              item.identifier && !item.type.hideCategoryIdentifiers ? (
                <>
                  <Identifier>{item.identifier}.</Identifier> {item.name}
                </>
              ) : (
                item.name
              )
            }
            iconImage={
              item.iconImage?.rendition?.src || item.parent?.iconImage?.rendition?.src || undefined
            }
            iconSvg={item.iconSvgUrl || item.parent?.iconSvgUrl || undefined}
            size={compact ? 'sm' : 'md'}
            themeColor="badgeColorNeutral"
            color={item.color || item.parent?.color || undefined}
            link={
              noLink ? undefined : (
                <CategoryLink
                  category={item}
                  categoryType={categoryType}
                  categoryTypeHeader={categoryTypeHeader}
                  ListLinkComponent={ListLinkComponent}
                  linkMode={linkMode}
                />
              )
            }
            maxLines={item.name.length > 50 ? 2 : 4}
          />
        </li>
      ))}
    </CategoryBadges>
  );
};

type CategoryTagLike = {
  id: string;
  identifier: string;
  name: string;
  helpText: string;
  color: string;
  iconSvgUrl: string | null;
  iconImage?: { rendition?: { src: string } | null } | null;
  categoryPage?: { urlPath: string } | null;
  level?: { name: string } | null;
  parent?: CategoryTagLike | null;
  type: { id: string; hideCategoryIdentifiers: boolean };
};

type CategoryTypeLike = {
  id: string;
  identifier: string;
  name?: string;
  helpText?: string;
  levels?: Array<{ name: string }>;
};

type CategoryTagsProps = {
  categories: CategoryTagLike[];
  types: CategoryTypeLike[];
  noLink?: boolean;
  compact?: boolean;
  ListLinkComponent?: ComponentType<ListLinkComponentProps>;
  linkMode?: CategoryLinkMode;
};

function CategoryTags(props: CategoryTagsProps) {
  const {
    categories,
    types,
    noLink = false,
    compact = false,
    ListLinkComponent,
    linkMode = 'default',
  } = props;

  const groupElements = types.map((ct) => {
    const cats = categories.filter((cat) => cat.type.id === ct.id);
    if (!cats || !cats.length) return null;
    /* If category type seems to have levels,
        use the level name of the first selected categoory
        as section header */

    const categoryTypeHeader =
      (ct.levels?.length ?? 0) > 0 && cats[0].level?.name ? cats[0].level.name : ct.name;

    return (
      <CategoryGroup key={ct.id} $compact={compact}>
        {categoryTypeHeader || (ct.helpText && ct.id) ? (
          <h3>
            {categoryTypeHeader}
            {ct.helpText && <PopoverTip content={ct.helpText} identifier={ct.id} />}
          </h3>
        ) : null}
        <CategoryContent
          categories={cats}
          categoryType={ct}
          categoryTypeHeader={categoryTypeHeader}
          noLink={noLink}
          compact={compact}
          ListLinkComponent={ListLinkComponent}
          linkMode={linkMode}
        />
      </CategoryGroup>
    );
  });

  return <Categories $compact={compact}>{groupElements}</Categories>;
}

CategoryTags.fragments = {
  category: CATEGORY_TAG_FRAGMENT,
  categoryType: CATEGORY_TYPE_FRAGMENT,
};

export default CategoryTags;
